import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import BarLoading from '../Loading/BarLoading';
import FilterSidebar from './FilterSidebar';
import MapView from './MapView';
import MobileSearchBar from './MobileSearchBar';
import './SaveSearch.css';
import SaveSearchModal from './SaveSearchModal';
import SearchResults from './SearchResults';
import './SearchResults.css';
import { filterAndSortProducts } from './searchUtils';
import './CategoryExclusionFilter.css';
import SortDropdown from './SortDropdown';
import VoiceSearch from './VoiceSearch';

export default function AdvancedSearch({
  products,
  loading,
  query,
  filters: initialFilters,
  onFiltersChange,
  onSortChange,
  viewMode: viewModeProp,
  onViewModeChange,
  onVoiceResult,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [filters, setFilters] = useState(initialFilters || {});
  const [sort, setSort] = useState(initialFilters?.sort || 'newest');
  const [viewMode, setViewModeState] = useState(viewModeProp || 'grid');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSearchText, setMobileSearchText] = useState(query || '');

  const setFiltersAndNotify = (next) => {
    setFilters(next);
    if (onFiltersChange) onFiltersChange(next, sort);
  };
  const setSortAndNotify = (next) => {
    setSort(next);
    if (onSortChange) onSortChange(next, filters);
  };
  const setViewMode = (mode) => {
    setViewModeState(mode);
    if (onViewModeChange) onViewModeChange(mode);
  };

  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0)
      setFilters(initialFilters);
    if (initialFilters?.sort) setSort(initialFilters.sort);
  }, [initialFilters]);

  useEffect(() => {
    setMobileSearchText(query || '');
  }, [query]);

  const handleMobileSearch = useCallback(
    (e) => {
      e.preventDefault();
      const trimmed = mobileSearchText.trim();
      if (trimmed) history.push(`/search?q=${encodeURIComponent(trimmed)}`);
      else history.push('/search');
    },
    [mobileSearchText, history]
  );

  const filteredByQuery = useMemo(() => {
    if (!products || !query || !query.trim()) return products || [];
    const q = query.trim().toLowerCase();
    return (products || []).filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [products, query]);

  const filteredAndSorted = useMemo(
    () => filterAndSortProducts(filteredByQuery, filters, sort),
    [filteredByQuery, filters, sort]
  );

  const locationText =
    [filters.city, filters.state].filter(Boolean).join(', ') || null;
  const activeFilterCount = Object.keys(filters).filter(
    (k) =>
      k !== 'sort' &&
      filters[k] !== '' &&
      filters[k] !== false &&
      filters[k] != null
  ).length;

  // Helper to remove a single excluded category
  const removeExcludedCategory = useCallback((category) => {
    const newExcluded = (filters.excludedCategories || []).filter(c => c !== category);
    setFiltersAndNotify({ ...filters, excludedCategories: newExcluded });
  }, [filters, setFiltersAndNotify]);

  return (
    <div className="advancedSearchLayout">
      <MobileSearchBar
        query={query}
        mobileSearchText={mobileSearchText}
        onMobileSearchTextChange={setMobileSearchText}
        onMobileSearchSubmit={handleMobileSearch}
        sort={sort}
        onSortChange={setSortAndNotify}
        filters={filters}
        onFiltersChange={setFiltersAndNotify}
        mobileFiltersOpen={mobileFiltersOpen}
        onMobileFiltersOpenChange={setMobileFiltersOpen}
        activeFilterCount={activeFilterCount}
      />
      <aside className="advancedSearchSidebar">
        <FilterSidebar filters={filters} onChange={setFiltersAndNotify} />
      </aside>
      <div className="advancedSearchMain">
        <div className="searchResultsHeader">
          <div className="searchResultsHeaderLeft">
            <span className="searchResultsCount">
              {t('search.results', { count: filteredAndSorted.length })}
              {query ? ` ${t('search.resultsFor', { query: `"${query}"` })}` : ''}
              {locationText
                ? ` ${t('search.in', { location: locationText })}`
                : ''}
            </span>
            {filters.excludedCategories && filters.excludedCategories.length > 0 && (
              <div className="excludedCategoriesTags">
                <span className="excludedCategoriesLabel">{t('search.excludeCategories')}:</span>
                {filters.excludedCategories.map((cat) => (
                  <span key={cat} className="excludedCategoryTag">
                    {t(`subcategories.${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`, cat)}
                    <button
                      type="button"
                      className="excludedCategoryRemove"
                      onClick={() => removeExcludedCategory(cat)}
                      aria-label={t('search.removeExclusion')}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="searchResultsHeaderRight">
            <div className="viewModeToggle">
              <button
                type="button"
                className={
                  viewMode === 'grid' ? 'viewModeBtn active' : 'viewModeBtn'
                }
                onClick={() => setViewMode('grid')}
                aria-label={t('search.gridView')}
              >
                &#10094;
              </button>
              <button
                type="button"
                className={
                  viewMode === 'list' ? 'viewModeBtn active' : 'viewModeBtn'
                }
                onClick={() => setViewMode('list')}
                aria-label={t('search.listView')}
              >
                &#8801;
              </button>
              <button
                type="button"
                className={
                  viewMode === 'map' ? 'viewModeBtn active' : 'viewModeBtn'
                }
                onClick={() => setViewMode('map')}
                aria-label={t('search.mapView')}
                title={t('search.listingsWithLocation')}
              >
                &#128196;
              </button>
            </div>
            <button
              type="button"
              className="saveSearchBtn"
              onClick={() => setShowSaveModal(true)}
            >
              {t('search.saveSearch')}
            </button>
            <SortDropdown value={sort} onChange={setSortAndNotify} />
          </div>
        </div>
        {loading ? (
          <BarLoading />
        ) : viewMode === 'map' ? (
          <MapView products={filteredAndSorted} />
        ) : (
          <SearchResults products={filteredAndSorted} viewMode={viewMode} />
        )}
      </div>
      {showSaveModal && (
        <SaveSearchModal
          query={query}
          filters={filters}
          onClose={() => setShowSaveModal(false)}
          onSuccess={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}

export { filterAndSortProducts };
